import { useEffect, useState, useCallback } from "react";
import { Plus, PencilSimple, Trash, X, Camera, FileText, ChartLineUp } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useTableSort } from "../lib/useTableSort";
import { SortHeader } from "../components/SortHeader";

const emptyForm = {
  id: null,
  name: "",
  cif: "",
  address: "",
  phone: "",
  notes: "",
  contact_person: "",
  contact_phone: "",
  order_day: "",
  supply_day: "",
  default_iva_pct: 10,
  payment_method: "transferencia",
};

const DAYS = [
  { v: "", label: "—" },
  { v: "lunes", label: "Lunes" },
  { v: "martes", label: "Martes" },
  { v: "miercoles", label: "Miércoles" },
  { v: "jueves", label: "Jueves" },
  { v: "viernes", label: "Viernes" },
  { v: "sabado", label: "Sábado" },
  { v: "domingo", label: "Domingo" },
];

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const { sortedRows: sortedSuppliers, sortKey, sortDir, toggleSort } = useTableSort(suppliers, "name", "asc");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/suppliers/${form.id}`, form);
        toast.success("Proveedor actualizado");
      } else {
        await api.post("/suppliers", form);
        toast.success("Proveedor creado");
      }
      setShowForm(false);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/suppliers/${confirmDel.id}`);
      toast.success("Proveedor eliminado");
      setConfirmDel(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    setUploading(true);
    setAnalysisResult(null);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        toast.info("Analizando factura con IA…");
        const { data } = await api.post("/invoices/analyze", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setAnalysisResult(data);
        toast.success(
          data.supplier_created
            ? `Proveedor "${data.supplier.name}" creado y factura guardada`
            : `Factura asignada a "${data.supplier.name}"`
        );
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        toast.info(`Analizando ${files.length} facturas con IA… esto puede tardar un poco.`);
        const { data } = await api.post("/invoices/analyze/batch", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const ok = data.results.filter((r) => r.ok).length;
        const dup = data.results.filter((r) => !r.ok && r.status === 409).length;
        const err = data.results.filter((r) => !r.ok && r.status !== 409).length;
        toast.success(`✅ ${ok} OK · ${dup} duplicadas · ${err} con error`);
      }
      load();
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="label-cap">gestión</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Proveedores</h1>
        </div>
        <div className="flex gap-3">
          <label
            data-testid="upload-invoice-btn"
            className={`btn-primary px-5 py-3 rounded-xl font-display font-bold flex items-center gap-2 cursor-pointer ${
              uploading ? "opacity-60 pointer-events-none" : ""
            }`}
          >
            <Camera size={18} weight="duotone" />
            {uploading ? "Analizando…" : "Cargar factura(s)"}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={onUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <button
            onClick={() => { setForm(emptyForm); setShowForm(true); }}
            data-testid="add-supplier-btn"
            className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-bold flex items-center gap-2 hover:bg-white"
          >
            <Plus size={18} weight="bold" /> Nuevo proveedor
          </button>
        </div>
      </div>

      {analysisResult && (
        <div className="card-flat p-6 mb-6 border-[var(--primary)]" data-testid="analysis-result">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-cap">factura procesada</div>
              <h2 className="font-display text-xl font-bold tracking-tight mb-1">
                {analysisResult.supplier.name}
              </h2>
              <div className="text-sm text-[var(--text-secondary)]">
                Nº {analysisResult.invoice.invoice_number || "—"} · {analysisResult.invoice.invoice_date || "—"} · Total {analysisResult.invoice.total ?? "—"} €
                {analysisResult.supplier_created && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-[var(--primary)] text-white">Nuevo</span>
                )}
              </div>
              <div className="text-sm text-[var(--text-secondary)] mt-1">
                {analysisResult.invoice.lines.length} línea(s) extraídas.
              </div>
            </div>
            <button
              onClick={() => setAnalysisResult(null)}
              className="p-2 rounded-full hover:bg-[var(--border)]"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-4 flex gap-2">
            <Link
              to={`/admin/suppliers/${analysisResult.supplier.id}`}
              className="btn-primary px-4 py-2 rounded-xl font-display font-semibold text-sm"
              data-testid="analysis-see-supplier"
            >
              Ver proveedor
            </Link>
          </div>
        </div>
      )}

      <div className="card-flat overflow-hidden" data-testid="suppliers-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left p-4"><SortHeader sortKey="name" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Nombre</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="cif" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>CIF</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="phone" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Teléfono</SortHeader></th>
              <th className="text-left p-4"><SortHeader sortKey="payment_method" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Pago</SortHeader></th>
              <th className="text-right p-4"><SortHeader align="right" sortKey="invoice_count" currentKey={sortKey} currentDir={sortDir} onClick={toggleSort}>Facturas</SortHeader></th>
              <th className="text-right p-4 label-cap">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sortedSuppliers.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center text-[var(--text-secondary)]">
                  Sin proveedores. Cárgales una factura o créales manualmente.
                </td>
              </tr>
            )}
            {sortedSuppliers.map((s) => (
              <tr key={s.id} className="border-b border-[var(--border)] last:border-0 hover:bg-[var(--bg)]">
                <td className="p-4 font-display font-semibold">
                  <Link to={`/admin/suppliers/${s.id}`} className="hover:text-[var(--primary)]" data-testid={`supplier-link-${s.id}`}>
                    {s.name}
                  </Link>
                </td>
                <td className="p-4 text-[var(--text-secondary)] font-mono text-sm">{s.cif || "—"}</td>
                <td className="p-4 text-[var(--text-secondary)]">{s.phone || "—"}</td>
                <td className="p-4">
                  {s.payment_method === "domiciliat" ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[var(--primary)]/15 text-[var(--primary)] text-xs font-display font-bold uppercase tracking-wider" data-testid={`pm-${s.id}`}>
                      Domiciliado
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-lg border border-[var(--border)] text-xs font-display font-semibold uppercase tracking-wider text-[var(--text-secondary)]" data-testid={`pm-${s.id}`}>
                      Transferencia
                    </span>
                  )}
                </td>
                <td className="p-4 text-right font-mono">
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[var(--bg)] border border-[var(--border)] text-sm">
                    <FileText size={14} weight="duotone" /> {s.invoice_count}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <Link
                      to={`/admin/suppliers/${s.id}`}
                      data-testid={`price-history-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white font-display text-sm font-semibold"
                    >
                      <ChartLineUp size={14} weight="duotone" /> Historial
                    </Link>
                    <button
                      onClick={() => { setForm({ ...s, payment_method: s.payment_method || "transferencia" }); setShowForm(true); }}
                      data-testid={`edit-supplier-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-white font-display text-sm font-semibold"
                    >
                      <PencilSimple size={14} weight="duotone" /> Editar
                    </button>
                    <button
                      onClick={() => setConfirmDel(s)}
                      data-testid={`delete-supplier-${s.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border)] hover:bg-[var(--danger)] hover:text-white hover:border-[var(--danger)] font-display text-sm font-semibold"
                    >
                      <Trash size={14} weight="duotone" /> Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-lg p-8 relative">
            <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[var(--border)] flex items-center justify-center">
              <X size={18} />
            </button>
            <div className="label-cap mb-2">{form.id ? "editar" : "nuevo"}</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mb-6">{form.id ? "Editar proveedor" : "Nuevo proveedor"}</h2>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { k: "name", label: "Nombre", req: true, col: 2 },
                  { k: "cif", label: "CIF / NIF" },
                  { k: "phone", label: "Teléfono del proveedor" },
                  { k: "address", label: "Dirección", col: 2 },
                  { k: "contact_person", label: "Persona de contacto" },
                  { k: "contact_phone", label: "Teléfono de contacto" },
                ].map(({ k, label, req, col }) => (
                  <div key={k} className={col === 2 ? "md:col-span-2" : ""}>
                    <label className="label-cap block mb-2">{label}{req && <span className="text-[var(--danger)] ml-1">*</span>}</label>
                    <input
                      required={req}
                      value={form[k] || ""}
                      onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                      data-testid={`supplier-${k}`}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)]"
                    />
                  </div>
                ))}
                <div>
                  <label className="label-cap block mb-2">Día de pedido</label>
                  <select
                    value={form.order_day || ""}
                    onChange={(e) => setForm((f) => ({ ...f, order_day: e.target.value }))}
                    data-testid="supplier-order-day"
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)]"
                  >
                    {DAYS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-cap block mb-2">Día de suministro</label>
                  <select
                    value={form.supply_day || ""}
                    onChange={(e) => setForm((f) => ({ ...f, supply_day: e.target.value }))}
                    data-testid="supplier-supply-day"
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)]"
                  >
                    {DAYS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-cap block mb-2">IVA por defecto (%)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="30"
                    value={form.default_iva_pct ?? 10}
                    onChange={(e) => setForm((f) => ({ ...f, default_iva_pct: e.target.value }))}
                    data-testid="supplier-iva"
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)]"
                  />
                  <div className="text-xs text-[var(--text-secondary)] mt-1">Usado para calcular precios sin IVA en búsquedas. Típico: 10% (hostelería), 21% (general), 4% (alimentos básicos).</div>
                </div>
                <div className="md:col-span-2">
                  <label className="label-cap block mb-2">Forma de pago</label>
                  <div className="flex gap-3">
                    <label className={`flex-1 cursor-pointer px-4 py-3 rounded-xl border font-display font-semibold text-center transition ${form.payment_method === "transferencia" ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-[var(--border)] hover:bg-white"}`}>
                      <input
                        type="radio"
                        name="payment_method"
                        value="transferencia"
                        checked={form.payment_method === "transferencia"}
                        onChange={() => setForm((f) => ({ ...f, payment_method: "transferencia" }))}
                        data-testid="supplier-pm-transferencia"
                        className="hidden"
                      />
                      Transferencia
                    </label>
                    <label className={`flex-1 cursor-pointer px-4 py-3 rounded-xl border font-display font-semibold text-center transition ${form.payment_method === "domiciliat" ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-[var(--border)] hover:bg-white"}`}>
                      <input
                        type="radio"
                        name="payment_method"
                        value="domiciliat"
                        checked={form.payment_method === "domiciliat"}
                        onChange={() => setForm((f) => ({ ...f, payment_method: "domiciliat" }))}
                        data-testid="supplier-pm-domiciliat"
                        className="hidden"
                      />
                      Domiciliado
                    </label>
                  </div>
                  <div className="text-xs text-[var(--text-secondary)] mt-1">Los proveedores domiciliados marcan sus facturas como pagadas automáticamente.</div>
                </div>
                <div className="md:col-span-2">
                  <label className="label-cap block mb-2">Notas</label>
                  <textarea
                    value={form.notes || ""}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-white focus:outline-none focus:border-[var(--primary)]"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
                <button type="submit" disabled={saving} data-testid="supplier-form-submit" className="btn-primary px-6 py-3 rounded-xl font-display font-bold disabled:opacity-50">
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-flat w-full max-w-md p-8">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-2">¿Eliminar a {confirmDel.name}?</h2>
            <p className="text-sm text-[var(--text-secondary)] mb-6">Se borrarán también todas sus facturas. Esta acción no se puede deshacer.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmDel(null)} className="px-5 py-3 rounded-xl border border-[var(--border)] font-display font-semibold">Cancelar</button>
              <button onClick={doDelete} className="btn-danger px-6 py-3 rounded-xl font-display font-bold">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
